// Direct client-side OpenAI calls.
import { recordChat, recordImage } from "./usageMeter";

const OPENAI_CHAT = "https://api.openai.com/v1/chat/completions";
const OPENAI_IMAGES = "https://api.openai.com/v1/images/generations";
const MODEL_CHAT = "gpt-4o-mini";
const MODEL_VISION = "gpt-4o";
const MODEL_IMAGE = "gpt-image-1";

async function callChat({ apiKey, system, user, temperature, jsonMode, maxTokens }) {
  if (!apiKey) throw new Error("Missing OpenAI API key");
  const body = {
    model: MODEL_CHAT,
    temperature,
    messages: [
      { role: "system", content: system },
      { role: "user", content: user },
    ],
  };
  if (maxTokens) body.max_tokens = maxTokens;
  if (jsonMode) body.response_format = { type: "json_object" };

  // Robust fetch wrapper:
  // - 3-minute timeout via AbortController (OpenAI's chat completions p99 is
  //   well under 60s, but the partner-tab prompt is large now after the
  //   intelligence-layer upgrade — we want room to spare without leaving
  //   the user staring at a silent spinner).
  // - One automatic retry on transient network failures ("Failed to fetch"
  //   from the browser, or 5xx from OpenAI's edge). Genuine 4xx errors
  //   (401 invalid key, 429 quota) are NOT retried — they're terminal.
  // - Clear error text so the UI can show something useful instead of the
  //   generic "Failed to fetch".
  const TIMEOUT_MS = 180_000;
  const doFetch = async () => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
    try {
      return await fetch(OPENAI_CHAT, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${apiKey}` },
        body: JSON.stringify(body),
        signal: controller.signal,
      });
    } finally {
      clearTimeout(timer);
    }
  };

  let res;
  try {
    res = await doFetch();
  } catch (e) {
    // Network-layer failure (DNS, CORS preflight, connection dropped,
    // browser tab throttled). Try ONCE more after a short pause — most
    // transient failures recover instantly.
    if (e?.name === "AbortError") {
      throw new Error("OpenAI request timed out (3 min). The prompt may be too large or your network is slow — please retry.");
    }
    await new Promise((r) => setTimeout(r, 1500));
    try {
      res = await doFetch();
    } catch (e2) {
      if (e2?.name === "AbortError") {
        throw new Error("OpenAI request timed out after retry. Please check your connection and retry.");
      }
      throw new Error(
        `Network call to OpenAI failed: ${e2?.message || e?.message || "unknown"}. Check your internet connection, VPN, or ad-blocker.`
      );
    }
  }

  // Retry 5xx once — those are transient on OpenAI's side.
  if (res.status >= 500 && res.status < 600) {
    await new Promise((r) => setTimeout(r, 1500));
    try {
      res = await doFetch();
    } catch (_) { /* fall through to the !res.ok branch below */ }
  }

  if (!res.ok) {
    let detail = "";
    try { const j = await res.json(); detail = j?.error?.message || ""; } catch (_) { /* body may not be JSON */ }
    if (res.status === 429) throw new Error("OpenAI rate limit / quota reached (429). Check billing at platform.openai.com.");
    if (res.status === 401) throw new Error("Invalid OpenAI key (401). Reset it from the header.");
    if (res.status === 400 && /context length|maximum context|tokens/i.test(detail)) {
      throw new Error("Prompt is too long for gpt-4o-mini's context window. Try a shorter audience-DNA blob or fewer trends.");
    }
    throw new Error(`OpenAI error ${res.status}: ${detail || res.statusText}`);
  }
  const data = await res.json();
  // Record exact spend from OpenAI's returned `usage` object. Wrapped in
  // try/catch so a meter glitch can never break a generation call.
  try {
    if (data?.usage) {
      recordChat({
        model: MODEL_CHAT,
        promptTokens: data.usage.prompt_tokens,
        completionTokens: data.usage.completion_tokens,
      });
    }
  } catch {
    /* ignore */
  }
  return data?.choices?.[0]?.message?.content || "";
}

export async function chatJSON({ apiKey, system, user, temperature = 0.8 }) {
  const content = await callChat({ apiKey, system, user, temperature, jsonMode: true });
  try { return JSON.parse(content || "{}"); }
  catch (e) { throw new Error("OpenAI returned invalid JSON"); }
}

export async function chatText({ apiKey, system, user, temperature = 0.7, maxTokens }) {
  return await callChat({ apiKey, system, user, temperature, jsonMode: false, maxTokens });
}

// ---- Image generation with in-image text overlay (gpt-image-1) ----
// `mode`:
//   - "nomad"  (default) → rotates through 4 digital-nomad lifestyle archetypes
//     using `variantSeed` (0..3). Used by Partner/Content post covers.
//   - "custom" → use the caller's `prompt` as the SCENE directly. No nomad overlay.
//     Used by the Offer product builder so covers match the actual niche.
export async function generateImage({
  apiKey,
  prompt,
  hook = "",
  size = "1024x1536",
  quality = "medium",
  variantSeed = 0,
  mode = "nomad",
}) {
  if (!apiKey) throw new Error("Missing OpenAI API key");

  if (mode === "nomad") {
    const VARIANTS = [
      {
        subject: "young woman on an open-air balcony in Chiang Mai, Thailand, overlooking palm trees and warm morning light, MacBook and iced coffee on a rattan table, she is smiling softly while scrolling her phone",
        vibe: "golden-hour sunrise, tropical warmth, freedom, iPhone-UGC realism, shallow depth of field",
      },
      {
        subject: "young man walking through Lisbon's Alfama district at dusk with a small backpack, looking over his shoulder with a confident smile, sun-bleached yellow walls and trams in the distance",
        vibe: "cinematic travel-vlog look, soft sunset glow, European golden streetlight, candid UGC feel",
      },
      {
        subject: "a couple laughing inside a modern co-working loft in Bali, big windows onto rice paddies, laptops open, iPhones on the desk, iced matcha and passport stamps visible",
        vibe: "natural daylight, relaxed productivity, mid-morning, Wes-Anderson-meets-Apple-ad clean palette",
      },
      {
        subject: "over-the-shoulder first-person view of a hand holding a phone showing a beach sunset in Vietnam, airplane wing visible through the window behind, boarding pass tucked inside a Moleskine notebook on the tray table",
        vibe: "iPhone-UGC travel content, warm orange/violet sky, movement, story-style composition",
      },
    ];
    const v = VARIANTS[((variantSeed % VARIANTS.length) + VARIANTS.length) % VARIANTS.length];
    const scene = `Scene: ${v.subject}. ${v.vibe}.`;
    const persona = "digital-nomad / make-money-online creator";

    const fullPrompt = [
      `Create a scroll-stopping VERTICAL (9:16) social-media COVER IMAGE for a ${persona}.`,
      scene,
      prompt ? `Creator's extra direction: ${prompt}` : "",
      `VISUAL STYLE: photoreal, magazine-editorial quality, shot on iPhone Pro, slight film grain, rich contrast. Avoid stock-photo smiling-into-camera clichés. Avoid AI-generated plastic skin. No visible brand logos. No Photoshop text except the overlay below.`,
      `COMPOSITION: subject placed to the lower/right, leaving CLEAR NEGATIVE SPACE in the upper-left third so the title caption below is fully readable.`,
      `TEXT OVERLAY (render this EXACT text prominently inside the image — bold sans-serif heavy-weight caption, high contrast, white text with a subtle drop shadow — placed in the negative space, NEVER cropped, NEVER overlapping the subject's face): "${(hook || "").slice(0, 110)}"`,
      `IMPORTANT: no watermark, no Pexels/Unsplash logo, no UI chrome, no speech bubbles.`,
    ].filter(Boolean).join("\n\n");

    return await callImage({ apiKey, fullPrompt, size, quality });
  }

  // ─── CUSTOM MODE ───
  // The caller supplies a complete scene via `prompt`. The scene LEADS the composition.
  // We strip any "digital-nomad / make-money-online" framing and hard-negate every
  // nomad visual so gpt-image-1 does not default to beaches/airports/coworking.
  const customFullPrompt = [
    `Create a scroll-stopping VERTICAL (9:16) editorial COVER IMAGE.`,
    prompt ? prompt : "",
    `VISUAL STYLE: photoreal, magazine-editorial quality, shot on a pro camera, slight film grain, rich contrast. No illustration, no 3D render, no cartoon. Avoid stock-photo smiling-into-camera clichés. Avoid AI-generated plastic skin. No visible brand logos.`,
    `COMPOSITION: leave CLEAR NEGATIVE SPACE in the upper-left third so the title caption is fully readable. Subject and props belong to the world described in the scene above.`,
    `TEXT OVERLAY (render this EXACT text prominently inside the image — bold sans-serif heavy-weight caption, high contrast, white text with a subtle drop shadow — placed in the negative space, NEVER cropped, NEVER overlapping the subject's face): "${(hook || "").slice(0, 110)}"`,
    // Hard negative constraints — gpt-image-1 defaults to digital-nomad/travel
    // when any "creator / online product / make-money" hint is in the prompt.
    // These negatives forcefully steer it back to the niche.
    `DO NOT include ANY of the following unless the scene explicitly calls for them:`,
    `· beaches, palm trees, ocean views, tropical resorts, Bali, Thailand, Chiang Mai, Lisbon, Vietnam, Mexico`,
    `· airports, airplanes, airplane windows, boarding passes, passports, luggage, suitcases`,
    `· digital-nomad / remote-worker tropes: laptops in cafes, MacBooks on balconies, iced coffee photography, co-working spaces`,
    `· maps, globes, world flags, travel-vlog aesthetics, Instagram-travel clichés`,
    `· generic "make money online" imagery, stock-trader monitors, dollar signs, hundred-dollar bills fans`,
    `· motivational posters, neon gradients, hologram effects, AI-generated futurism`,
    `INSTEAD, the image must be 100% grounded in the specific niche described in the scene above — the props, people, clothing, tools, colours and environment must all belong to that niche's real world.`,
    `IMPORTANT: no watermark, no Pexels/Unsplash logo, no UI chrome, no speech bubbles, no fake text other than the caption.`,
  ].filter(Boolean).join("\n\n");

  return await callImage({ apiKey, fullPrompt: customFullPrompt, size, quality });
}

// Shared image-call helper.
async function callImage({ apiKey, fullPrompt, size, quality }) {  const res = await fetch(OPENAI_IMAGES, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${apiKey}` },
    body: JSON.stringify({ model: MODEL_IMAGE, prompt: fullPrompt, size, quality, n: 1 }),
  });
  if (!res.ok) {
    let detail = "";
    try { const j = await res.json(); detail = j?.error?.message || ""; } catch (_) { /* body may not be JSON */ }
    if (res.status === 429) throw new Error("OpenAI rate limit / quota (429). Top up billing and retry.");
    if (res.status === 401) throw new Error("Invalid OpenAI key (401).");
    if (res.status === 403) throw new Error("Your account needs verified access to gpt-image-1. Enable it at platform.openai.com/settings/organization/general.");
    throw new Error(`Image error ${res.status}: ${detail || res.statusText}`);
  }
  const data = await res.json();
  // Estimated image spend (gpt-image-1 doesn't return a usage field).
  try {
    recordImage({ size, quality, count: 1 });
  } catch {
    /* ignore */
  }
  const item = data?.data?.[0];
  if (item?.b64_json) return { dataUrl: `data:image/png;base64,${item.b64_json}` };
  if (item?.url) return { dataUrl: item.url };
  throw new Error("No image returned");
}

// ---- Scene image (used by the in-browser video generator) ----
// Clean pass-through: the scene's image_prompt drives the composition.
// No text overlay, no hook burn-in, no niche-specific negative constraints.
// Produces a 9:16 (1024x1536) photoreal image suitable for canvas playback.
export async function generateSceneImage({ apiKey, prompt, size = "1024x1536", quality = "medium" }) {
  if (!apiKey) throw new Error("Missing OpenAI API key");
  if (!prompt) throw new Error("Missing scene prompt");
  const fullPrompt = [
    `Create a VERTICAL 9:16 photoreal scene for a short-form social video.`,
    `Scene: ${prompt}`,
    `STYLE: photoreal, shot on a pro camera, natural light, slight film grain, rich contrast, one clear subject, cinematic composition. No illustration. No 3D render. No cartoon. No AI-plastic skin.`,
    `COMPOSITION: clean frame, subject clearly visible, room at top and bottom so a caption can be placed over the image later without cropping the subject.`,
    `HARD NO-TEXT RULE — the image MUST NOT contain ANY of the following: text, words, letters, captions, lettering, typography, signage, billboards, neon signs, road signs, bus signs, license plates, store fronts with readable text, books with readable titles, screen UI, app interfaces, websites, logos, brand marks, watermarks, social-media overlays, speech bubbles, subtitles, AI-generated text artifacts, or any glyphs that could resemble writing. If the scene naturally contains a sign or screen, BLUR it out or angle it away so no readable text is visible. The frame is purely visual storytelling.`,
  ].join("\n\n");
  return await callImage({ apiKey, fullPrompt, size, quality });
}

// ---------- STATIC AD (Pinterest Optimised) ----------
// Generates the brand-level static ad image. Caller passes the already-wrapped
// fullPrompt (built via wrapStaticAdImagePrompt in lib/prompts.js).
export async function generateStaticAdImage({ apiKey, fullPrompt, size = "1024x1536", quality = "high" }) {
  if (!apiKey) throw new Error("Missing OpenAI API key");
  if (!fullPrompt) throw new Error("Missing static ad prompt");
  return await callImage({ apiKey, fullPrompt, size, quality });
}

// ---------- VISION ANALYSIS (gpt-4o multimodal) ----------
// Analyses a user-uploaded photo and returns the Pinterest overlay concept
// shape (same fields as buildStaticAdPrompt) so the existing static-ad UI
// + browser compositor can render it without changes.
//
// `imageDataUrl` MUST be a data URL (data:image/...;base64,...) — the photo
// is sent inline to the OpenAI vision endpoint. Falls back to retry on
// transient network errors, returns parsed JSON.
export async function analyzeImageWithVision({ apiKey, imageDataUrl, system, user }) {
  if (!apiKey) throw new Error("Missing OpenAI API key");
  if (!imageDataUrl) throw new Error("Missing image data URL");
  if (!system || !user) throw new Error("Missing vision prompt");

  const body = {
    model: MODEL_VISION,
    temperature: 0.8,
    response_format: { type: "json_object" },
    messages: [
      { role: "system", content: system },
      {
        role: "user",
        content: [
          { type: "text", text: user },
          { type: "image_url", image_url: { url: imageDataUrl, detail: "high" } },
        ],
      },
    ],
  };

  const TIMEOUT_MS = 180_000;
  const doFetch = async () => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
    try {
      return await fetch(OPENAI_CHAT, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${apiKey}` },
        body: JSON.stringify(body),
        signal: controller.signal,
      });
    } finally {
      clearTimeout(timer);
    }
  };

  let res;
  try {
    res = await doFetch();
  } catch (e) {
    if (e?.name === "AbortError") throw new Error("Vision request timed out (3 min).");
    await new Promise((r) => setTimeout(r, 1500));
    res = await doFetch();
  }

  if (!res.ok) {
    let detail = "";
    try { const j = await res.json(); detail = j?.error?.message || ""; } catch (_) { /* */ }
    if (res.status === 429) throw new Error("OpenAI rate limit / quota (429).");
    if (res.status === 401) throw new Error("Invalid OpenAI key (401).");
    if (res.status === 403) throw new Error("Your account needs access to gpt-4o. Enable it at platform.openai.com.");
    throw new Error(`Vision error ${res.status}: ${detail || res.statusText}`);
  }
  const data = await res.json();
  try {
    if (data?.usage) {
      recordChat({
        model: MODEL_VISION,
        promptTokens: data.usage.prompt_tokens,
        completionTokens: data.usage.completion_tokens,
      });
    }
  } catch { /* ignore */ }
  const content = data?.choices?.[0]?.message?.content || "";
  try { return JSON.parse(content || "{}"); }
  catch { throw new Error("Vision returned invalid JSON"); }
}


